import React from 'react';
import { connect } from 'react-redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import { Field, reduxForm, initialize, change, formValueSelector } from 'redux-form';
import { Row, Col, Container } from 'react-bootstrap';
import cx from 'classnames';
import { injectIntl } from 'react-intl';
import s from './ManageNotificationsForm.css';
import submit from './submit';
import validate from './validate';
import Loader from '../../Common/Loader';
import NotificationBannerUpload from './NotificationBannerUpload';
import Select from 'react-select';

const FCM_TITLE_LIMIT = 65;
const FCM_BODY_LIMIT = 240;

// Sample values used in the live preview when personalized mode is on
const PREVIEW_FIRST_NAME = 'John';
const PREVIEW_LAST_NAME = 'Doe';

function interpolatePreview(text) {
  if (!text) return text;
  return text
    .replace(/\{\{firstName\}\}/g, PREVIEW_FIRST_NAME)
    .replace(/\{\{lastName\}\}/g, PREVIEW_LAST_NAME);
}

// ── Twitter-style circular counter ───────────────────────────────────────────
function CircleCounter({ current, limit }) {
  const remaining = limit - current;
  const pct = Math.min(current / limit, 1);
  const r = 10;
  const circumference = 2 * Math.PI * r;
  const dash = pct * circumference;

  let color = '#cfd9de';
  if (remaining <= 20 && remaining > 0) color = '#ffd400';
  if (remaining <= 0) color = '#f4212e';

  return (
    <span className={s.circleCounter} title={`${remaining} characters remaining`}>
      <svg width="26" height="26" viewBox="0 0 26 26">
        {/* track */}
        <circle cx="13" cy="13" r={r} fill="none" stroke="#e7e7e7" strokeWidth="2.5" />
        {/* fill */}
        <circle
          cx="13" cy="13" r={r}
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeDasharray={`${dash} ${circumference}`}
          strokeLinecap="round"
          transform="rotate(-90 13 13)"
        />
      </svg>
      {remaining <= 20 && (
        <span className={s.circleCounterNum} style={{ color }}>
          {remaining}
        </span>
      )}
    </span>
  );
}

// ── Device previews ───────────────────────────────────────────────────────────
const AppIcon = () => (
  <div className={s.previewIcon}>A</div>
);

function AndroidPreview({ title, body, imageUrl }) {
  return (
    <div className={s.previewDevice}>
      <p className={s.previewLabel}>Android</p>
      <div className={s.androidPhone}>
        <div className={s.androidStatusBar} />
        <div className={s.androidNotification}>
          <div className={s.notifRow}>
            <AppIcon />
            <div className={s.notifContent}>
              <div className={s.notifAppName}>ASAP · now</div>
              <div className={s.notifTitle}>{title || 'Notification title'}</div>
              <div className={s.notifBody}>{body || 'Your message will appear here.'}</div>
            </div>
          </div>
          {imageUrl && (
            <img src={imageUrl} alt="banner" className={s.notifBannerImage} />
          )}
        </div>
        <div className={s.androidHomeBar} />
      </div>
    </div>
  );
}

function IphonePreview({ title, body, imageUrl }) {
  return (
    <div className={s.previewDevice}>
      <p className={s.previewLabel}>iPhone</p>
      <div className={s.iphonePhone}>
        <div className={s.iphoneStatusBar}>
          <span className={s.iphoneTime}>9:41</span>
          <div className={s.iphoneNotch} />
          <span className={s.iphoneStatusIcons}>▶ 100%</span>
        </div>
        <div className={s.iphoneNotification}>
          <div className={s.iphoneNotifHeader}>
            <AppIcon />
            <span className={s.iphoneAppName}>ASAP</span>
            <span className={s.iphoneNotifTime}>now</span>
          </div>
          <div className={s.iphoneNotifTitle}>{title || 'Notification title'}</div>
          <div className={s.iphoneNotifBody}>{body || 'Your message will appear here.'}</div>
          {imageUrl && (
            <img src={imageUrl} alt="banner" className={s.notifBannerImage} />
          )}
        </div>
        <div className={s.iphoneHomeIndicator} />
      </div>
    </div>
  );
}

// ── Form ──────────────────────────────────────────────────────────────────────
export class ManageNotificationsForm extends React.Component {
  static defaultProps = {
    loading: false,
    siteName: 'ASAP: Food & Grocery Delivery',
  };

  componentDidMount() {
    const { initialize, siteName } = this.props;
    initialize({ to: '1', messageType: 'pushNotification', title: siteName, sendMode: 'now', isPersonalizedNotification: false });
  }

  componentDidUpdate(prevProps) {
    const { siteName, change } = this.props;
    if (siteName !== prevProps.siteName) {
      change('title', siteName);
    }
  }

  // Custom render for title field (with inline counter)
  renderTitleField = ({ input, meta: { touched, error } }) => {
    const { formatMessage } = this.props.intl;
    const len = (input.value || '').length;
    const remaining = FCM_TITLE_LIMIT - len;
    return (
      <div className={s.fieldWrap}>
        <div className={s.inputWithCounter}>
          <input
            {...input}
            type="text"
            className={cx('form-control', s.formInput, touched && error && s.inputError)}
            placeholder="e.g. Big promo this weekend! 🎉"
            maxLength={FCM_TITLE_LIMIT}
          />
          <span className={cx(s.inlineCounter, len > FCM_TITLE_LIMIT - 10 && s.inlineCounterWarn, len >= FCM_TITLE_LIMIT && s.inlineCounterOver)}>
            {remaining}
          </span>
        </div>
        {touched && error && <span className={s.errorMsg}>{formatMessage(error)}</span>}
      </div>
    );
  }

  // Custom render for body field (with circular counter)
  renderBodyField = ({ input, meta: { touched, error } }) => {
    const { formatMessage } = this.props.intl;
    const len = (input.value || '').length;
    return (
      <div className={s.fieldWrap}>
        <div className={s.textareaWrap}>
          <textarea
            {...input}
            rows={5}
            className={cx('form-control', s.bodyTextarea, touched && error && s.inputError)}
            placeholder="Write your notification message here…"
            maxLength={FCM_BODY_LIMIT}
          />
          <div className={s.circleCounterWrapper}>
            <CircleCounter current={len} limit={FCM_BODY_LIMIT} />
          </div>
        </div>
        {touched && error && <span className={s.errorMsg}>{formatMessage(error)}</span>}
      </div>
    );
  }

  // Custom render for scheduledAt field
  renderScheduledAtField = ({ input, meta: { touched, error } }) => {
    const { formatMessage } = this.props.intl;
    return (
      <div className={s.fieldWrap}>
        <input
          {...input}
          type="datetime-local"
          className={cx('form-control', s.formInput, touched && error && s.inputError)}
        />
        {touched && error && <span className={s.errorMsg}>{formatMessage(error)}</span>}
      </div>
    );
  }

  // Custom render for react-select field
  renderSelectField = ({ input, options, label, placeholder, meta: { touched, error } }) => {
    const { formatMessage } = this.props.intl;
    
    // Handle multiple values for react-select
    const value = input.value && Array.isArray(input.value) 
      ? options.filter(opt => input.value.includes(opt.value))
      : [];

    return (
      <div className={s.fieldWrap}>
        <Select
          {...input}
          isMulti
          value={value}
          onChange={(opts) => input.onChange(opts ? opts.map(o => o.value) : [])}
          onBlur={() => input.onBlur(input.value)}
          options={options}
          placeholder={placeholder || 'Select areas...'}
          classNamePrefix="react-select"
          isSearchable
          styles={{
            control: (base, state) => ({
              ...base,
              borderRadius: '10px',
              border: state.isFocused ? '1.5px solid #E62529' : '1.5px solid #e0e0e0',
              boxShadow: state.isFocused ? '0 0 0 3px rgba(230, 37, 41, 0.08)' : 'none',
              '&:hover': {
                borderColor: state.isFocused ? '#E62529' : '#d0d0d0',
              },
              minHeight: '44px',
              cursor: 'pointer',
              fontSize: '14px',
            }),
            option: (base, state) => ({
              ...base,
              backgroundColor: state.isSelected ? '#E62529' : state.isFocused ? 'rgba(230, 37, 41, 0.05)' : 'transparent',
              color: state.isSelected ? '#fff' : '#1c1c1e',
              cursor: 'pointer',
              fontSize: '14px',
              '&:active': {
                backgroundColor: '#E62529',
                color: '#fff',
              },
            }),
            placeholder: (base) => ({
              ...base,
              color: '#999',
            }),
            singleValue: (base) => ({
              ...base,
              color: '#1c1c1e',
            }),
          }}
        />
        {touched && error && <span className={s.errorMsg}>{formatMessage(error)}</span>}
      </div>
    );
  }

  render() {
    const { handleSubmit, loading, submitting, titleValue, messageValue, sendModeValue, imageUrlValue, isPersonalizedValue } = this.props;

    const isScheduled = sendModeValue === 'later';
    const previewTitle = isPersonalizedValue ? interpolatePreview(titleValue) : titleValue;
    const previewBody = isPersonalizedValue ? interpolatePreview(messageValue) : messageValue;


    return (
      <div className={s.formCard}>
        <Container fluid className="p-0">
          <form onSubmit={handleSubmit(submit)}>
            <Row className="g-0">

              {/* ── Left: form fields ────────────────────────────── */}
              <Col lg={7} md={12} className={s.formLeft}>

                {/* Target */}
                <div className={s.formSection}>
                  <label className={s.fieldLabel}>Send to</label>
                  <div className={s.segmentGroup}>
                    {[
                      { value: 'all', label: '👥 All' },
                      { value: '1', label: '🛍 Customers' },
                      { value: '2', label: '🛵 Drivers' },
                      { value: '3', label: '🏪 Shops' },
                    ].map(({ value, label }) => (
                      <label key={value} className={s.segmentItem}>
                        <Field name="to" component="input" type="radio" value={value} className={s.hiddenRadio} />
                        <span className={s.segmentLabel}>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Area Dropdown */}
                <div className={s.formSection}>
                  <label className={s.fieldLabel}>Select Area</label>
                  <Field
                    name="locationIds"
                    component={this.renderSelectField}
                    options={(this.props.locations || []).map((loc) => ({
                      value: String(loc.id),
                      label: `📍 ${loc.locationName}`,
                    }))}
                  />
                  <span className={s.fieldHint}>· Filter recipients based on their current location coordinates.</span>
                </div>

                {/* Title */}
                <div className={s.formSection}>
                  <label className={s.fieldLabel}>
                    Notification Title
                    <span className={s.fieldHint}>· max {FCM_TITLE_LIMIT} chars</span>
                  </label>
                  <Field name="title" component={this.renderTitleField} />
                </div>

                {/* Body */}
                <div className={s.formSection}>
                  <label className={s.fieldLabel}>
                    Message Body
                    <span className={s.fieldHint}>· max {FCM_BODY_LIMIT} chars</span>
                  </label>
                  <Field name="message" component={this.renderBodyField} />
                </div>

                {/* Personalized notification toggle */}
                <div className={s.formSection}>
                  <div className={s.personalizedRow}>
                    <div className={s.personalizedLabel}>
                      <span className={s.personalizedTitle}>Personalized Notification</span>
                      <span className={s.personalizedDesc}>Insert recipient's name using {'{{firstName}}'} or {'{{lastName}}'}</span>
                    </div>
                    <label className={s.toggleSwitch}>
                      <Field name="isPersonalizedNotification" component="input" type="checkbox" />
                      <span className={s.toggleSlider} />
                    </label>
                  </div>
                  {isPersonalizedValue && (
                    <div className={s.personalizedHint}>
                      Use <strong>{'{{firstName}}'}</strong> and <strong>{'{{lastName}}'}</strong> in the title or body to personalize each message.
                      Example: <em>"Hi {'{{firstName}}'}, don't miss our promo today!"</em>
                      <br />
                      The preview shows sample values: <strong>{PREVIEW_FIRST_NAME} {PREVIEW_LAST_NAME}</strong>.
                      Messages are sent individually to each recipient.
                    </div>
                  )}
                </div>

                {/* Image Banner */}
                <div className={s.formSection}>
                  <label className={s.fieldLabel}>
                    Image Banner
                    <span className={s.fieldHint}>· optional</span>
                  </label>
                  <NotificationBannerUpload
                    value={imageUrlValue}
                    onChange={(url) => this.props.change('imageUrl', url)}
                  />
                </div>

                {/* Send mode toggle */}
                <div className={s.formSection}>
                  <label className={s.fieldLabel}>When to send</label>
                  <div className={s.sendModeGroup}>
                    <label className={s.sendModeItem}>
                      <Field name="sendMode" component="input" type="radio" value="now" className={s.hiddenRadio} />
                      <span className={s.sendModeLabel}>
                        <span className={s.sendModeIcon}>⚡</span>
                        <span>
                          <strong>Send Now</strong>
                          <small>Deliver immediately</small>
                        </span>
                      </span>
                    </label>
                    <label className={s.sendModeItem}>
                      <Field name="sendMode" component="input" type="radio" value="later" className={s.hiddenRadio} />
                      <span className={s.sendModeLabel}>
                        <span className={s.sendModeIcon}>🗓</span>
                        <span>
                          <strong>Schedule</strong>
                          <small>Pick a date & time</small>
                        </span>
                      </span>
                    </label>
                  </div>
                </div>

                {/* Datetime picker (only when scheduling) */}
                {isScheduled && (
                  <div className={cx(s.formSection, s.scheduleDateSection)}>
                    <label className={s.fieldLabel}>Scheduled Date & Time</label>
                    <Field name="scheduledAt" component={this.renderScheduledAtField} />
                    <p className={s.fieldHint2}>
                      Uses your local timezone — stored as UTC automatically.
                    </p>
                  </div>
                )}

                {/* Submit */}
                <div className={s.formSection}>
                  <Loader
                    type="button"
                    label={isScheduled ? '🗓  Schedule Notification' : '⚡  Send Now'}
                    show={loading}
                    buttonType="submit"
                    className={cx(s.submitBtn)}
                    disabled={submitting || loading}
                    isSuffix={true}
                  />
                </div>

              </Col>

              {/* ── Right: preview ───────────────────────────────── */}
              <Col lg={5} md={12} className={s.formRight}>
                <div className={s.previewStickyWrap}>
                  <div className={s.previewHeader}>
                    <span className={s.previewHeaderDot} />
                    Live Preview
                  </div>
                  <div className={s.previewWrapper}>
                    <AndroidPreview title={previewTitle} body={previewBody} imageUrl={imageUrlValue} />
                    <IphonePreview title={previewTitle} body={previewBody} imageUrl={imageUrlValue} />
                  </div>
                </div>
              </Col>

            </Row>
          </form>
        </Container>
      </div>
    );
  }
}

const ManageNotificationsFormRedux = reduxForm({
  form: 'ManageNotificationsForm',
  onSubmit: submit,
  validate,
})(ManageNotificationsForm);

const selector = formValueSelector('ManageNotificationsForm');

const mapState = (state) => ({
  loading: state.loader.sendNotification,
  siteName: state.siteSettings?.data?.siteName,
  titleValue: selector(state, 'title'),
  messageValue: selector(state, 'message'),
  sendModeValue: selector(state, 'sendMode'),
  imageUrlValue: selector(state, 'imageUrl'),
  isPersonalizedValue: selector(state, 'isPersonalizedNotification'),
});

const mapDispatch = { initialize, change };

export default injectIntl(withStyles(s)(connect(mapState, mapDispatch)(ManageNotificationsFormRedux)));
